import React, { createContext, useContext, useState } from 'react';

// Creating the context without type annotation
const LoadingContext = createContext();

/**
 * Custom hook to access the loading context
 * 
 * @returns {Object} The loading context including isLoading, loadingMessage, showLoading, hideLoading, blurBGCont.
 * @throws Will throw an error if the hook is used outside of an LoadingProvider.
 * 
 * @category Context
 */
export const useLoading = () => {
    const context = useContext(LoadingContext);
    if (!context) {
        throw new Error('useLoading must be used within a LoadingProvider');
    }
    return context;
};

/**
 * Provides app access to loading context
 * 
 * @param {React.ReactNode} props.children - children components to be wrapped by the provider
 * 
 * @returns {JSX.Element}
 * 
 * @category Context
 */
export const LoadingProvider = ({ children }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [blurBGCont, setBlurBGCont] = useState(false);

    //for operations that may take some time, just show spinner on the page, without it blocking UI
    const [showingSpinner, setShowingSpinner] = useState(false);
    const [spinnerMessage, setSpinnerMessage] = useState(false);

    const showLoading = (message = '', blur = true) => {
        setIsLoading(true);
        setLoadingMessage(message);
        setBlurBGCont(blur);
    };

    const showSpinner = (message = '') => {
        setShowingSpinner(true)
        setSpinnerMessage(message)
    }
 
    const hideLoading = () => {
        setIsLoading(false);
        setLoadingMessage('');
        setBlurBGCont(false);
    };

    const hideSpinner = () => {
        setShowingSpinner(false)
        setSpinnerMessage('')
    }

    const contextValue = {
        isLoading,
        loadingMessage,
        showLoading,
        hideLoading,
        blurBGCont,

        showingSpinner,
        showSpinner,
        hideSpinner,
        spinnerMessage
    };

    return (
        <LoadingContext.Provider value={contextValue}>
            {children}
        </LoadingContext.Provider>
    );
};
